/**
 * Editorial theme system.
 *
 * A self-contained palette that is independent of the `@v1/ui` HSL token
 * system. The four source colours also resolve two semantic roles:
 * `--theme-surface-dark` and `--theme-accent-text`. The ThemeSwitcher swaps
 * the active palette at runtime and the choice persists in localStorage.
 * Default = claret.
 *
 * The default palette is applied to <html> by the root layout. The provider
 * then restores a saved palette so it survives client navigation.
 */

export type Theme = {
  id: string;
  name: string;
  dark: string;
  accent: string;
  ground: string;
  ink: string;
  darkSurfaceRole: "dark" | "ink";
  accentTextRole: "dark" | "ink";
};

export const THEMES: Theme[] = [
  {
    id: "claret",
    name: "Claret",
    dark: "#4A1324",
    accent: "#F4D9B5",
    ground: "#F2EDE3",
    ink: "#1B1913",
    darkSurfaceRole: "dark",
    accentTextRole: "dark",
  },
  {
    id: "deep-teal",
    name: "Deep teal · seafoam",
    dark: "#0F4D47",
    accent: "#D1F9F1",
    ground: "#F2EDE3",
    ink: "#1B1913",
    darkSurfaceRole: "dark",
    accentTextRole: "dark",
  },
  {
    id: "ink-mint",
    name: "Ink · mint",
    dark: "#1B1913",
    accent: "#C8E6D0",
    ground: "#F2EDE3",
    ink: "#1B1913",
    darkSurfaceRole: "dark",
    accentTextRole: "dark",
  },
  {
    id: "lavender",
    name: "Canvas · lavender",
    dark: "#4F3F8A",
    accent: "#D6CDEB",
    ground: "#EFE9F5",
    ink: "#1B1913",
    darkSurfaceRole: "dark",
    accentTextRole: "dark",
  },
  {
    id: "seafoam-canvas",
    name: "Canvas · seafoam",
    dark: "#0F4D47",
    accent: "#86D4C5",
    ground: "#D1F9F1",
    ink: "#0F4D47",
    darkSurfaceRole: "dark",
    accentTextRole: "dark",
  },
  {
    id: "orange",
    name: "Vibrant orange",
    dark: "#D2480F",
    accent: "#FFE0A5",
    ground: "#FFF5E5",
    ink: "#2A1410",
    darkSurfaceRole: "ink",
    accentTextRole: "ink",
  },
];

export const DEFAULT_THEME_ID = "claret";
/** The home (mlx.systems `/`) now opens in claret, in lockstep with the
 *  rest of the editorial system. Kept as its own seam so the home can be
 *  re-pointed at a distinct brand accent later without touching callers. */
export const HOME_DEFAULT_THEME_ID = "claret";
export const STORAGE_KEY = "mlx-one-pager-theme";

/** Mix a theme colour with transparency via color-mix(). */
export function mix(varName: string, pct: number): string {
  return `color-mix(in srgb, var(${varName}) ${pct}%, transparent)`;
}

export const DARK = "var(--theme-dark)";
export const ACCENT = "var(--theme-accent)";
export const GROUND = "var(--theme-ground)";
export const INK = "var(--theme-ink)";
export const SURFACE_DARK = "var(--theme-surface-dark)";
export const ACCENT_TEXT = "var(--theme-accent-text)";

export function resolveThemeRoles(theme: Theme) {
  return {
    surfaceDark: theme[theme.darkSurfaceRole],
    accentText: theme[theme.accentTextRole],
  };
}

/* Display face — Figtree (a geometric sans), driven via CSS variable. */
export const DISPLAY = { fontFamily: "var(--font-figtree)" } as const;

/** Label class — uppercase + wide tracking, Regular weight. */
export const LABEL_CLS = "text-[11px] font-medium uppercase tracking-[0.22em]";

/** Shared Tailwind rails keep editorial sections aligned without hiding the
 * architectural rules and instrument-specific geometry in utility strings. */
export const EDITORIAL_CONTAINER_CLS =
  "mx-auto w-full max-w-screen-xl px-5 sm:px-6 md:px-14";
export const EDITORIAL_SECTION_CLS = `${EDITORIAL_CONTAINER_CLS} py-14 sm:py-16 md:py-24`;
export const SECTION_TITLE_CLS =
  "text-[2.4rem] font-semibold leading-[1.04] tracking-[-0.03em] sm:text-[3rem] md:text-[3.75rem]";
export const SECTION_INTRO_CLS =
  "max-w-[60ch] text-[1.05rem] leading-[1.65] md:text-[1.15rem]";
export const DATA_LABEL_CLS =
  "text-[length:var(--type-label-small)] font-semibold tracking-[0.15em] uppercase";

export const NOISE_DATA_URI =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.85'/></svg>";

/**
 * Inline pre-paint script. Reads the stored palette id and sets the source
 * colours plus semantic role properties on <html> before first paint, so a
 * returning visitor never sees a flash of the default (claret) palette
 * before hydration. Drop into the document <head>.
 */
const THEME_RGB_MAP: Record<
  string,
  [string, string, string, string, string, string]
> =
  Object.fromEntries(
    THEMES.map((t) => {
      const roles = resolveThemeRoles(t);
      return [
        t.id,
        [
          t.dark,
          t.accent,
          t.ground,
          t.ink,
          roles.surfaceDark,
          roles.accentText,
        ],
      ];
    }),
  ) as Record<string, [string, string, string, string, string, string]>;

export const EDITORIAL_THEME_INIT = `(function(){try{var P=${JSON.stringify(
  THEME_RGB_MAP,
)};var id=localStorage.getItem(${JSON.stringify(STORAGE_KEY)});if(!id){id=location.pathname===${JSON.stringify(
  "/",
)}?${JSON.stringify(HOME_DEFAULT_THEME_ID)}:${JSON.stringify(
  DEFAULT_THEME_ID,
)};}var p=P[id]||P[${JSON.stringify(
  DEFAULT_THEME_ID,
)}];if(!p)return;var d=document.documentElement;d.style.setProperty('--theme-dark',p[0]);d.style.setProperty('--theme-accent',p[1]);d.style.setProperty('--theme-ground',p[2]);d.style.setProperty('--theme-ink',p[3]);d.style.setProperty('--theme-surface-dark',p[4]);d.style.setProperty('--theme-accent-text',p[5]);}catch(e){}})();`;
